import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import AddIcon from "@mui/icons-material/Add";
import { Button, CircularProgress, Stack, Typography } from "@mui/material";

import { RoleName } from "constants";
import { useClientEntityAllocationActions } from "store";

import { ClientEntitiesTable } from "./ClientEntitiesTable";
import { AddEntityModal } from "./AddEntityModal";
import { RemoveEntityDialog } from "./RemoveEntityDialog";

import type { IEntity } from "./EntitiesTab.types";

/**
 * Entities tab for the client detail page.
 *
 * UI States:
 *   S-1 (Loading): renders skeleton rows inside a Stack.
 *   S-3 (Empty):   shows a message + "Add Entity" button (Admin/Team Member only).
 *   S-5 (Error):   shows "Couldn't load entities." + Retry button.
 *   Normal:        renders the ClientEntitiesTable.
 *
 * Inline Editing (AC-9, EC-5):
 *   - Click pencil icon -> cell becomes a TextField (pre-filled with the current value).
 *   - Press Enter / blur -> fires a PATCH request.
 *     - Success: shows a success toast, reverts the cell.
 *     - Failure: shows an error toast, reverts the cell to its original value.
 *   - Press Escape -> cancels edit mode, discards the pending value, reverts the cell.
 *
 * Remove Entity Dialog (AC-8, EC-3):
 *   - Click trash icon -> opens a MUI Dialog with the entity name and exact copy.
 *   - Cancel -> closes the dialog, row stays unchanged.
 *   - Remove -> fires a DELETE request, removes the row from the table,
 *     shows a confirmation toast ("Entity removed").
 *
 * RBAC (AC-10):
 *   - Admin or Team Member: see the "Add Entity" button, and the edit/remove icons on each row.
 *   - Team Viewer: strictly hides the "Add Entity" button, the edit icon, and the remove icon.
 */
interface EntitiesTabProps {
  /** Unique client identifier. */
  clientId: number;
  /** Current user role name — used to gate edit/remove operations. */
  roleId: string | null;
}

export const EntitiesTab: React.FC<EntitiesTabProps> = ({ clientId, roleId }) => {
  const {
    getAllocations,
    updateAllocation,
    deleteAllocation
  } = useClientEntityAllocationActions();

  // -- Data state --
  const [entities, setEntities] = useState<IEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // -- Fetch data --
  const fetchEntities = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const data = await getAllocations(clientId);
      setEntities(data);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, [clientId, getAllocations]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  // -- Auth helper --
  const canManage = useMemo(() => {
    if (roleId === null) return false;
    return roleId === RoleName.Admin || roleId === RoleName.Analyst;
  }, [roleId]);

  // -- State --
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [editingEntityId, setEditingEntityId] = useState<number | null>(null);
  const [removeDialogOpen, setRemoveDialogOpen] = useState<boolean>(false);
  const [removeTargetId, setRemoveTargetId] = useState<number | null>(null);

  const removeTargetEntity = useMemo(
    () => entities.find((e) => e.id === removeTargetId) ?? null,
    [entities, removeTargetId]
  );

  const handleRetry = useCallback(() => {
    fetchEntities();
  }, [fetchEntities]);

  // -- Allocation update (inline editing -- AC-9, EC-5) --

  const handleAllocationUpdate = useCallback(
    async (entityId: number, newPercentage: number) => {
      try {
        await updateAllocation(entityId, { allocation_percentage: newPercentage });
        toast.success("Allocation updated");
        setEditingEntityId(null);
        // Update local state optimistically
        setEntities((prev) =>
          prev.map((e) =>
            e.id === entityId ? { ...e, allocation_percentage: newPercentage } : e
          )
        );
      } catch (err: any) {
        toast.error(err?.message ?? "Failed to update allocation.");
        // Revert edit mode so the cell goes back to read-only with original value
        setEditingEntityId(null);
      }
    },
    [updateAllocation]
  );

  // -- Remove entity (AC-8, EC-3) --

  const handleRemoveClick = useCallback((entityId: number) => {
    setRemoveTargetId(entityId);
    setRemoveDialogOpen(true);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoveDialogOpen(false);
    if (removeTargetId === null) return;

    try {
      await deleteAllocation(removeTargetId);
      toast.success("Entity removed");
      // Remove the row from local state
      setEntities((prev) => prev.filter((e) => e.id !== removeTargetId));
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to remove entity.");
    }
  }, [removeTargetId, deleteAllocation]);

  const handleRemoveCancel = useCallback(() => {
    setRemoveDialogOpen(false);
  }, []);

  // -- Inline edit helpers --

  const handleEditOpen = useCallback((entityId: number) => {
    setEditingEntityId(entityId);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingEntityId(null);
  }, []);

  // -- Add Entity modal handlers --

  const handleAddEntity = useCallback(() => {
    setAddModalOpen(true);
  }, []);

  const handleAddEntitySuccess = useCallback(() => {
    toast.success("Entity linked");
    fetchEntities();
  }, [fetchEntities]);

  // -- Render --
  return (
    <Stack sx={{ gap: "16px", width: "100%", height: "100%" }}>
      {/* Header */}
      <Typography
        variant="h6"
        sx={{ fontSize: "18px", color: "#071F35", fontWeight: 500 }}
      >
        Entities
      </Typography>

      {/* White panel wrapper for content */}
      <Stack
        sx={{
          bgcolor: "#fff",
          borderRadius: "16px",
          boxShadow: "0px 1px 3px rgba(0,0,0,0.1)",
          p: "24px",
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          flexGrow: 1,
          overflow: "hidden",
        }}
      >
        {/* Toolbar: Add Entity button */}
        {canManage && (
          <Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <Button
              variant="contained"
              onClick={handleAddEntity}
              startIcon={<AddIcon />}
              sx={{
                height: "27px",
                fontSize: "14px",
                textTransform: "none",
                fontWeight: 400,
                bgcolor: "#071F35",
                "&:hover": { bgcolor: "#0a3142" },
              }}
            >
              Add Entity
            </Button>
          </Stack>
        )}

        {/* S-1: Loading -- CircularProgress */}
        {isLoading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4 }}
          >
            <CircularProgress size={24} />
          </Stack>
        ) : entities.length === 0 && !hasError ? (
          /* S-3: Empty state */
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ py: 4, gap: 1 }}
          >
            <Typography sx={{ color: "#898989", fontSize: "14px" }}>
              No entities linked yet. Add an investment entity to connect this
              client to their deals and files.
            </Typography>
            {canManage && (
              <Button
                variant="contained"
                onClick={handleAddEntity}
                startIcon={<AddIcon />}
                sx={{
                  height: "27px",
                  fontSize: "14px",
                  textTransform: "none",
                  fontWeight: 400,
                  bgcolor: "#071F35",
                  "&:hover": { bgcolor: "#0a3142" },
                }}
              >
                Add Entity
              </Button>
            )}
          </Stack>
        ) : hasError ? (
          /* S-5: Error state */
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              py: 4,
              gap: 1,
              border: "1px solid #ffcdd2",
              borderRadius: "8px",
              bgcolor: "#fff5f5",
            }}
          >
            <Typography sx={{ color: "#d32f2f", fontSize: "14px" }}>
              Couldn&apos;t load entities. Please try again.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleRetry}
              sx={{ textTransform: "none", fontSize: "12px" }}
            >
              Retry
            </Button>
          </Stack>
        ) : (
          /* Normal state: render the table */
          <ClientEntitiesTable
            entities={entities}
            roleId={roleId}
            editingEntityId={editingEntityId}
            onAllocationUpdate={handleAllocationUpdate}
            onRemoveEntity={handleRemoveClick}
            onEditCancel={handleEditCancel}
            onEditOpen={handleEditOpen}
          />
        )}
      </Stack>

      {/* Remove entity confirmation dialog */}
      <RemoveEntityDialog
        open={removeDialogOpen}
        entityName={removeTargetEntity?.investor_name ?? "this entity"}
        linkedDealsCount={removeTargetEntity?.linked_deals_count ?? 0}
        onConfirm={handleRemoveConfirm}
        onCancel={handleRemoveCancel}
      />

      {/* Add Entity modal */}
      <AddEntityModal
        clientId={clientId}
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSuccess={handleAddEntitySuccess}
      />
    </Stack>
  );
};

